import React, { useCallback, useEffect, useState } from 'react';

import {
  ContactList,
  ContactListItem,
  ContactListRefProps,
} from './ContactList';
import ContactListSkeleton from './ContactListSkeleton';
import useContactSearch from './useContactSearch';
import { makePrioStyles } from '../../../theme/utils';
import {
  ContactSearchType,
  ContactId,
  CompanyId,
  OfficeId,
} from '../../../models/Types';
import classNames from 'classnames';
import { ContactProps } from './ContactSelectionList';
import { useContactListActiveItem } from '../hooks/useContactListActiveItem';
import { Contact } from '../../../models/Contact';
import { Company } from '../../../models/Company';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  contactList: {
    borderTop: theme.old.borders.content,
  },
  contactListItem: {
    '& .contact-list-item-actions': {
      visibility: 'hidden',
    },
    '&:hover': {
      '& .contact-list-item-actions': {
        visibility: 'visible',
      },
    },
  },
}));

interface ContactSearchProps {
  className?: string;
  type: ContactSearchType;
  searchTerm: string;
  onContactClick?: (contactId: ContactId) => void;
  onCompanyClick?: (companyId: CompanyId) => void;
  searchTypeChanged?: (isOnline: boolean) => void;
  officeId?: OfficeId;
  isOnlineFromOuter?: boolean;
  selectedContactProp?: ContactProps;
  includeArchived?: boolean;
  dontSetActiveIndex?: boolean;
  searchAnyway?: boolean;
  setSearchAnyway?: (value: boolean) => void;
}

export const ContactSearch: React.FC<ContactSearchProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const {
    className,
    type,
    searchTerm,
    onContactClick,
    onCompanyClick,
    officeId,
    searchTypeChanged,
    isOnlineFromOuter,
    selectedContactProp = undefined,
    includeArchived = false,
    dontSetActiveIndex = false,
    searchAnyway = false,
    setSearchAnyway = () => {},
  } = props;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isOnline, setIsOnline] = useState<boolean>(false);

  const [items, isFetching, propSearchItemCount] = useContactSearch({
    searchTerm,
    type,
    selectedContactProp,
    officeId: officeId ?? undefined,
    isOnline,
    includeArchived,
    searchAnyway,
  });

  const contactListRef = React.useRef<ContactListRefProps>(null);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getId = useCallback((value: ContactListItem) => {
    if (!value) {
      return '';
    }
    if (value.type === 'contact') {
      return (value.value as Contact).contactId;
    }
    if (value.type === 'company') {
      return (value.value as Company).companyId;
    }
    return '';
  }, []);
  //#endregion

  //#region ------------------------------ Effects
  const { setRef } = useContactListActiveItem(
    searchTerm,
    items,
    getId,
    dontSetActiveIndex
  );

  useEffect(() => {
    if (isOnlineFromOuter !== undefined) {
      setIsOnline(isOnlineFromOuter);
    }
  }, [isOnlineFromOuter]);

  useEffect(() => {
    if (searchTerm === '') {
      setIsOnline(false);
      if (searchTypeChanged) {
        searchTypeChanged(false);
      }
    }
  }, [searchTerm, searchTypeChanged]);
  //#endregion

  if (items.length === 0 && isFetching) {
    return <ContactListSkeleton />;
  }

  return (
    <ContactList
      ref={(ref) => {
        contactListRef.current = ref;
        setRef(ref);
      }}
      items={items}
      propSearchItemCount={propSearchItemCount}
      className={classNames(classes.contactList, className)}
      listItemClassName={classes.contactListItem}
      onContactClick={onContactClick}
      onCompanyClick={onCompanyClick}
      searchAnyway={searchAnyway}
      setSearchAnyway={setSearchAnyway}
    />
  );
};

export default ContactSearch;
